import * as React from 'react';
import { Field, Form, Formik } from 'formik';
import { AppUser } from 'Src/views/App/App';
import * as Yup from 'yup';
import { LEAGUE_AGGREGATION_TYPE, LEAGUE_AGGREGATION_TYPE_SERVER_TO_CLIENT_MAPPING } from 'Src/consts/league';
import { PUBLIC_SITE_STATUS_OPTIONS_SERVER_TO_CLIENT_MAPPING } from 'Src/consts/tournament';
import propz from 'propz';
import { SchoolLeague } from 'Src/models/schoolLeague';
import { useState } from 'react';
import { uploadImage } from 'Src/helpers/service/image';
import { Switch } from 'Src/components/Switch/Switch';
import './SchoolLeagueForm.scss';

interface Props {
  league?: SchoolLeague;
  onSubmit: (data) => void;
  onCancel: () => void;
  user: AppUser;
}

const SchoolLeagueSchema = Yup.object().shape({
  name: Yup.string().required('Required'),
  domain: Yup.string()
    .required('Required')
    .matches(/[!^a-z0-9\-]+$/, 'Should contain only lowercase characters and digits')
});

export const SchoolLeagueForm: React.FunctionComponent<Props> = props => {
  const { league, onSubmit, onCancel, user } = props;

  const [picUrl, setPicUrl] = useState(propz.get(league, ['photos', '0', 'picUrl'], ''));
  const [isImageError, setIsImageError] = useState(false);

  const isLeagueExist = typeof league !== 'undefined';

  const leagueForm = {
    name: isLeagueExist ? league.name : '',
    description: isLeagueExist ? league.description : '',
    aggregationType: isLeagueExist ? league.aggregationType : LEAGUE_AGGREGATION_TYPE.TEAM,
    domain: isLeagueExist ? league.domain : '',
    status: isLeagueExist ? league.publicSite.status : PUBLIC_SITE_STATUS_OPTIONS_SERVER_TO_CLIENT_MAPPING.DISABLED,
    password: isLeagueExist ? league.publicSite.password : '',
    isDisplayHousePoints: isLeagueExist ? propz.get(league, ['isDisplayHousePoints'], false) : false
  };

  const onImageSelected = event => {
    const image = event.target.files[0];

    uploadImage(user, image)
      .then(response => {
        const picUrl = `${window.apiImg}/images/${response.key}`;
        setPicUrl(picUrl);
        setIsImageError(false);
      })
      .catch(error => {
        console.error(error);
        setIsImageError(true);
      });
  };

  const onRemoveImageClick = (): void => {
    setPicUrl('');
  };

  const isPicUrlExists = typeof picUrl !== 'undefined' && picUrl.length > 0;
  const imgInputClassName = isImageError ? 'form-control-file is-invalid' : 'form-control-file';

  return (
    <div className="leagueForm">
      <Formik
        initialValues={leagueForm}
        validationSchema={SchoolLeagueSchema}
        onSubmit={values => {
          const { password, status, domain, ...rest } = values;
          const dataToSubmit = {
            ...rest,
            publicSite: {
              status: values.status,
              password: values.password
            },
            photos: picUrl.length > 0 ? [{ picUrl: picUrl }] : [],
            domain: domain.toLowerCase()
          };
          if (
            values.status !== PUBLIC_SITE_STATUS_OPTIONS_SERVER_TO_CLIENT_MAPPING.PROTECTED ||
            values.password === ''
          ) {
            delete dataToSubmit.publicSite.password;
          }

          onSubmit(dataToSubmit);
        }}
      >
        {({ touched, errors, setFieldValue, values }) => (
          <Form className="leagueForm__form">
            <section className="leagueForm__section">
              <header>
                <h3>League details</h3>
                <p>Add the name and description staff will use to identify this league.</p>
              </header>
              <div className="leagueForm__grid">
                <label className="leagueForm__field">
                  <span>Name</span>
                  <Field
                    type="text"
                    name="name"
                    className="leagueForm__control"
                    placeholder="e.g. Inter-house athletics league"
                  />
                  {touched.name && errors.name ? <small className="leagueForm__error">{errors.name}</small> : null}
                </label>
                <label className="leagueForm__field leagueForm__field--wide">
                  <span>
                    Description <em>Optional</em>
                  </span>
                  <Field
                    component="textarea"
                    rows="3"
                    name="description"
                    className="leagueForm__control"
                    placeholder="Describe the purpose of this league"
                  />
                </label>
              </div>
            </section>

            <section className="leagueForm__section">
              <header>
                <h3>Scoring setup</h3>
                <p>Choose how results are grouped and displayed on the leaderboard.</p>
              </header>
              <div className="leagueForm__grid">
                <label className="leagueForm__field">
                  <span>Aggregation type</span>
                  <span className="leagueForm__select">
                    <Field component="select" name="aggregationType" className="leagueForm__control">
                      <option value={LEAGUE_AGGREGATION_TYPE.HOUSE}>
                        {LEAGUE_AGGREGATION_TYPE_SERVER_TO_CLIENT_MAPPING.HOUSE}
                      </option>
                      <option value={LEAGUE_AGGREGATION_TYPE.TEAM}>
                        {LEAGUE_AGGREGATION_TYPE_SERVER_TO_CLIENT_MAPPING.TEAM}
                      </option>
                      <option value={LEAGUE_AGGREGATION_TYPE.INDIVIDUAL}>
                        {LEAGUE_AGGREGATION_TYPE_SERVER_TO_CLIENT_MAPPING.INDIVIDUAL}
                      </option>
                    </Field>
                  </span>
                </label>
              </div>
              {values.aggregationType === LEAGUE_AGGREGATION_TYPE.INDIVIDUAL && (
                <div className="leagueForm__switchRow">
                  <Field name="isDisplayHousePoints">
                    {({ field }) => (
                      <Switch
                        {...field}
                        value={values.isDisplayHousePoints}
                        customClass=""
                        onChange={event => {
                          const isDisplayHousePoints = event.target.checked;
                          setFieldValue('isDisplayHousePoints', isDisplayHousePoints);
                        }}
                      />
                    )}
                  </Field>
                  <div>
                    <strong>Display house points</strong>
                    <small>Show house totals on the public league website.</small>
                  </div>
                </div>
              )}
            </section>

            <section className="leagueForm__section">
              <header>
                <h3>Public website</h3>
                <p>Configure the address and who can view the league website.</p>
              </header>
              <div className="leagueForm__grid">
                <label className="leagueForm__field">
                  <span>Domain name</span>
                  <Field type="text" name="domain" className="leagueForm__control" placeholder="school-league-name" />
                  <small className="leagueForm__hint">Use lowercase letters, numbers and hyphens only.</small>
                  {touched.domain && errors.domain ? (
                    <small className="leagueForm__error">{errors.domain}</small>
                  ) : null}
                </label>
                <label className="leagueForm__field">
                  <span>Public site access</span>
                  <span className="leagueForm__select">
                    <Field component="select" name="status" className="leagueForm__control">
                      <option value={PUBLIC_SITE_STATUS_OPTIONS_SERVER_TO_CLIENT_MAPPING.DISABLED}>
                        Not published
                      </option>
                      <option value={PUBLIC_SITE_STATUS_OPTIONS_SERVER_TO_CLIENT_MAPPING.PUBLIC_AVAILABLE}>
                        Public
                      </option>
                      <option value={PUBLIC_SITE_STATUS_OPTIONS_SERVER_TO_CLIENT_MAPPING.PROTECTED}>
                        Password protected
                      </option>
                    </Field>
                  </span>
                </label>
                {values.status === PUBLIC_SITE_STATUS_OPTIONS_SERVER_TO_CLIENT_MAPPING.PROTECTED && (
                  <label className="leagueForm__field">
                    <span>Access password</span>
                    <Field type="text" name="password" className="leagueForm__control" />
                  </label>
                )}
              </div>
            </section>

            <section className="leagueForm__section">
              <header>
                <h3>Cover image</h3>
                <p>Use a wide 3:1 image for the best result on the public website.</p>
              </header>
              <label className="leagueForm__upload">
                <span>Choose image</span>
                <input type="file" name="picUrl" className={imgInputClassName} onChange={onImageSelected} />
              </label>
              {isImageError && <small className="leagueForm__error">Error uploading image</small>}
              {isPicUrlExists && (
                <div className="leagueForm__preview">
                  <img src={picUrl} alt="League cover" />
                  <button type="button" onClick={onRemoveImageClick}>
                    Remove image
                  </button>
                </div>
              )}
            </section>

            <div className="leagueForm__actions">
              <button type="button" onClick={onCancel}>
                Cancel
              </button>
              <button type="submit" className="is-primary">
                {isLeagueExist ? 'Save changes' : 'Create league'}
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

SchoolLeagueForm.displayName = 'SchoolLeagueForm';
